import { api } from "./api";
import { driverApi } from "./driverApi";

const installationId = () => {
  const key = "vb_native_installation";
  let value = localStorage.getItem(key);
  if (!value) {
    value = `vbi_${crypto.randomUUID()}`;
    localStorage.setItem(key, value);
  }
  return value;
};

const plugin = () => window.Capacitor?.Plugins?.PushNotifications || null;
let initializedFor = "";
let registration = null;
let generation = 0;
let handles = [];

export async function registerNativePush(principal) {
  const push = plugin();
  if (!push) return false;
  if (initializedFor === principal) return true;
  if (registration) return registration;
  const version = generation;
  const current = () => generation === version;
  const task = (async () => {
    await Promise.all(handles.splice(0).map((handle) => handle.remove()));
    const permission = await push.requestPermissions();
    if (!current() || permission.receive !== "granted") return false;
    await push.createChannel?.({ id: "voor_trip_updates", name: "Trip updates",
      description: "Assignments, arrivals, and active trip updates",
      importance: 5, visibility: 1, vibration: true });
    let finish, fail;
    const confirmed = new Promise((resolve, reject) => { finish = resolve; fail = reject; });
    // Attach rejection handling immediately, including failures during listener setup.
    confirmed.catch(() => {});
    const timer = setTimeout(() => fail(new Error("Push registration timed out.")), 20000);
    const listen = async (event, callback) => {
      const handle = await push.addListener(event, callback);
      if (!current()) { await handle.remove(); throw new Error("Session changed."); }
      handles.push(handle);
    };
    try {
      await listen("registrationError", () => fail(new Error("Push registration failed.")));
      await listen("registration", async ({ value }) => {
        if (!current()) return;
        try {
          const payload = { token: value, installation_id: installationId(), platform: "android" };
          if (principal === "driver") await driverApi.registerDevice(payload);
          else await api.post("/mobile/runtime/customer/device", payload);
          if (current()) finish(true);
        } catch { fail(new Error("Push device could not be registered.")); }
      });
      await listen("pushNotificationReceived", () => { if (current()) emitRuntimeRefreshSignal("push_signal"); });
      await listen("pushNotificationActionPerformed", ({ notification }) => {
        if (!current()) return;
        if (principal === "driver") { emitRuntimeRefreshSignal("push_signal"); return; }
        const route = notification?.data?.route;
        if (typeof route === "string" && route.startsWith("/") && !route.startsWith("//")) window.location.assign(route);
      });
      await push.register();
      await confirmed;
      if (!current()) return false;
      initializedFor = principal;
      return true;
    } finally { clearTimeout(timer); }
  })();
  registration = task;
  try { return await task; }
  finally { if (registration === task) registration = null; }
}

export function resetNativePushRegistration() {
  initializedFor = "";
  generation += 1;
  registration = null;
  for (const handle of handles.splice(0)) Promise.resolve(handle.remove()).catch(() => {});
}

export async function revokeNativePush(principal) {
  const id = installationId();
  if (principal === "driver") {
    const token = (await import("./driverApi")).driverSession.get();
    await api.delete(`/mobile/runtime/driver/device/${encodeURIComponent(id)}`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: false,
    });
  } else {
    await api.delete(`/mobile/runtime/customer/device/${encodeURIComponent(id)}`);
  }
  resetNativePushRegistration();
}
